/**
 * Journey workspace member-visible product chrome (UX-05-A).
 * Domain enums / routes stay unchanged; this module is display-only.
 */

export const WORKSPACE_PRODUCT_CHROME = Object.freeze({
  eyebrow: '旅程工作區',
  myJourneys: '我的行程',
  backToMyJourneys: '返回我的行程',
  overview: '總覽',
  editBasicInfo: '編輯基本資訊',
  title: '標題',
  description: '說明',
  destination: '目的地',
  dates: '日期',
  startDate: '開始日期',
  endDate: '結束日期',
  status: '狀態',
  yourRole: '你的角色',
  summary: '摘要',
  days: '天數',
  peopleCount: '人數',
  people: '旅伴',
  peopleEmpty: '這趟旅程尚未列出成員。',
  created: '建立時間',
  updated: '更新時間',
  noDescription: '尚未填寫說明。',
  notSet: '尚未設定',
  saveChanges: '儲存變更',
  saving: '儲存中…',
  cancel: '取消',
  tryAgain: '再試一次',
  planNextTitle: '接著安排行程',
  planning: '規劃',
  stays: '住宿',
  transport: '交通',
  checklist: '清單',
  budget: '預算',
  loading: '載入旅程工作區…',
  notFoundTitle: '找不到此行程',
  notFoundBody: '此行程可能不存在，或目前無法查看。',
  forbiddenTitle: '無法開啟此行程',
  forbiddenBody: '你沒有權限查看這趟私人行程。',
  openFailedTitle: '無法開啟此行程',

  // PD-COV-01: journey cover (PATCH coverFileReference — existing API)
  coverSectionLabel: '行程封面',
  coverEmpty: '尚未設定行程封面。',
  coverAlt: '行程封面',
  coverUploadLabel: '上傳封面圖片',
  coverUploading: '上傳中…',

  // logistics 共用動作（住宿／交通／清單）
  refresh: '重新整理',
  edit: '編輯',
  delete: '刪除',
  deleting: '刪除中…',
  add: '新增',
  referencePrefix: '編號',
  optionalSummaryPlaceholder: '選填文字摘要',
  formCheckFallback: '請檢查表單內容。',
  permissionDeniedChange: '你沒有權限進行此變更。',

  // 住宿
  addStay: '新增住宿',
  createStay: '建立住宿',
  saveStay: '儲存住宿',
  staysEmptyTitle: '尚未新增住宿',
  staysEmptyDescription: '為這趟旅程加入住宿安排。',
  staysPanelLead: '整理入住與退房安排，方便同行者對齊行程。',
  staysDatesNotSet: '尚未設定入住／退房時間',
  staysLocationUnset: '尚未填寫地點',
  staysDeleteTitle: '刪除這筆住宿？',
  staysDeleteBody: '「{name}」將永久移除。',
  staysPropertyName: '住宿名稱',
  staysCheckIn: '入住時間',
  staysCheckOut: '退房時間',
  staysMapLocation: '地圖位置（選填）',
  staysLocationLabel: '地點標籤',
  staysAddress: '地址',
  staysBookingReference: '訂房編號',
  staysNote: '備註',
  staysChangeFailed: '無法更新住宿，請再試一次。',
  staysLoadDetailFailed: '無法載入住宿詳情。',

  // 交通
  addTransport: '新增交通',
  createTransport: '建立交通',
  saveTransport: '儲存交通',
  transportEmptyTitle: '尚未新增交通',
  transportEmptyDescription: '為這趟旅程加入交通安排。',
  transportPanelLead: '記錄出發與抵達資訊，讓交通銜接一目了然。',
  transportTimesNotSet: '尚未設定出發／抵達時間',
  transportRouteUnset: '尚未填寫出發／抵達地',
  transportRouteSeparator: '→',
  transportDeleteTitle: '刪除這筆交通？',
  transportDeleteBody: '「{name}」將永久移除。',
  transportMode: '交通方式',
  transportModeUnspecified: '未指定',
  transportModeFlight: '飛機',
  transportModeTrain: '火車',
  transportModeBus: '巴士',
  transportModeFerry: '渡輪',
  transportModeCar: '汽車',
  transportModeTaxi: '計程車',
  transportModeWalk: '步行',
  transportModeOther: '其他',
  transportDepartureLocation: '出發地',
  transportArrivalLocation: '抵達地',
  transportDeparture: '出發時間',
  transportArrival: '抵達時間',
  transportCarrier: '承運／營運商',
  transportBookingReference: '訂位編號',
  transportNote: '備註',
  transportChangeFailed: '無法更新交通，請再試一次。',
  transportLoadDetailFailed: '無法載入交通詳情。',

  // 清單
  checklistLoading: '載入清單中…',
  checklistNewItem: '新項目',
  checklistNewItemPlaceholder: '例如：確認訂房',
  checklistAddItem: '新增項目',
  checklistAdding: '新增中…',
  checklistFilterAll: '全部',
  checklistFilterOpen: '未完成',
  checklistFilterCompleted: '已完成',
  checklistFilterAria: '清單篩選',
  checklistFilterEmptyTitle: '此篩選條件下沒有項目',
  checklistFilterEmptyDescription: '切換篩選條件以查看其他項目。',
  checklistStatusOpen: '未完成',
  checklistStatusCompleted: '已完成',
  checklistItemSaving: '儲存中…',
  checklistCompleteAria: '標記完成：{title}',
  checklistReopenAria: '重新開啟：{title}',
  checklistDeleteTitle: '刪除此項目？',
  checklistDeleteBody: '「{name}」將永久移除。',
  checklistProgressEmpty: '已完成 0 項',
  checklistProgress: '已完成 {completed} / {total} 項',
  checklistItemNotFound: '找不到此清單項目。',
  checklistChangeFailed: '無法更新清單，請再試一次。',
});

/** 以 `{key}` 佔位符填入產品文案。 */
export function fillWorkspaceProductChrome(template, values = {}) {
  return String(template || '').replace(/\{(\w+)\}/g, (_, key) => (
    values[key] == null ? '' : String(values[key])
  ));
}

const TRANSPORT_MODE_PRODUCT_LABELS = Object.freeze({
  FLIGHT: 'transportModeFlight',
  TRAIN: 'transportModeTrain',
  BUS: 'transportModeBus',
  FERRY: 'transportModeFerry',
  CAR: 'transportModeCar',
  TAXI: 'transportModeTaxi',
  WALK: 'transportModeWalk',
  OTHER: 'transportModeOther',
});

/** 交通方式 domain value → 產品語標籤（未知值保留原文）。 */
export function logisticsTransportModeProductLabel(mode) {
  const raw = String(mode || '').trim();
  if (!raw) return WORKSPACE_PRODUCT_CHROME.transportModeUnspecified;
  const chromeKey = TRANSPORT_MODE_PRODUCT_LABELS[raw];
  if (chromeKey) return WORKSPACE_PRODUCT_CHROME[chromeKey];
  return raw;
}

/**
 * Decisions 頁面產品 chrome（UX Epic 4 Slice 2）。
 * Domain enum / API value 不變；僅供顯示。
 * 用詞與 workspace nav「決定」對齊。
 */
export const DECISIONS_PRODUCT_CHROME = Object.freeze({
  title: '決定',
  backToDecisions: '返回決定',
  createDecision: '建立決定',
  createFirst: '建立第一個決定',
  createDraft: '建立草稿',
  creating: '建立中…',
  untitledJourney: '行程',
  emptyTitle: '還沒有決定',
  emptyDescription: '旅伴可以一起決定行程中的臨時狀況與選項。',
  sectionActive: '進行中',
  sectionHistory: '已結束',
  activeEmpty: '目前沒有進行中的決定。',
  historyEmpty: '尚無已結束的決定。',
  createLifecycleGate: '旅程需為「準備出發」「旅程進行中」或「旅程已完成」狀態，才能建立決定。',
  statusDraft: '草稿',
  statusOpen: '投票中',
  statusClosed: '已結案',
  statusCancelled: '已取消',
  deadlinePrefix: '截止',
  optionCount: '{count} 個選項',
  participatedCount: '{count} 人已參與',
  youVoted: '你已投票',
  resolved: '已決議',
  eligibilityOwnersOnly: '僅擁有者／協調者',
  eligibilityAllMembers: '所有有效旅伴',
  deadlineOptional: '截止時間（選填）',
  loadingDecision: '載入決定中…',
  decisionNotFoundTitle: '找不到此決定',
  decisionNotFoundBody: '此決定可能不存在，或目前無法查看。',
  openCreateFailedTitle: '無法開啟建立決定',
  openDetailFailedTitle: '無法開啟此決定',
  tryAgain: '再試一次',
  sessionExpired: '登入已過期，請重新登入。',
  formCheckFallback: '請檢查表單內容。',
  createPermissionDenied: '你沒有權限建立此決定。',
  createLifecycleDenied: '目前旅程狀態無法建立決定。',
  createIdempotencyConflict: '此次建立與先前請求衝突，請調整後再試。',
  createFailed: '無法建立決定，請再試一次。',
  journeyLoadFailed: '無法載入行程。',
  decisionLoadFailed: '無法載入決定。',
  optionsHeading: '選項',
  editDraft: '編輯草稿',
  saveDraft: '儲存草稿',
  cancelEdit: '取消編輯',
  openForVoting: '開放投票',
  openForVotingConfirmTitle: '要開放投票嗎？',
  openForVotingConfirmBody: '開放投票後，選項內容將無法修改。',
  confirmOpen: '確認開放',
  opening: '開放中…',
  cancelDecision: '取消決定',
  cancelDecisionConfirmTitle: '要取消此決定嗎？',
  confirmCancel: '確認取消',
  cancelling: '取消中…',
  dialogBack: '返回',
  votingHeading: '投票',
  deadlinePassed: '截止時間已過。投票操作已停用；後端為最終依據。',
  submitVote: '送出投票',
  changeVote: '改票',
  submitting: '送出中…',
  closeDecision: '結案',
  closeConfirmTitle: '要結案並產生結果嗎？',
  closeConfirmBody: '結案後將依目前票數產生結果（平票時可能沒有勝者）。不會自動修改行程活動。',
  confirmClose: '確認結案',
  closing: '結案中…',
  resultsHeading: '結果',
  eligible: '可參與人數',
  participated: '已參與',
  abstain: '棄權',
  outcome: '結果',
  outcomeTie: '平票 — 沒有單一勝者',
  participationThreshold: '參與門檻',
  thresholdMet: '已達標',
  thresholdNotMet: '未達標',
  voteCount: '{count} 票',
  resolutionHeading: '決議',
  cancelledHeading: '已取消',
  closedHint: '決定已完成，你可以回到行程手動調整安排。',
  voteChangeForbiddenHint: '此決定不允許改票。',
});

const DECISION_STATUS_PRODUCT_LABELS = Object.freeze({
  DRAFT: 'statusDraft',
  OPEN: 'statusOpen',
  CLOSED: 'statusClosed',
  CANCELLED: 'statusCancelled',
});

/** 決定狀態 domain value → 產品語標籤（未知值保留原文）。 */
export function decisionStatusProductLabel(status) {
  const raw = String(status || '').trim();
  if (!raw) return '';
  const chromeKey = DECISION_STATUS_PRODUCT_LABELS[raw];
  if (chromeKey) return DECISIONS_PRODUCT_CHROME[chromeKey];
  return raw;
}

export const MY_JOURNEYS_PRODUCT_CHROME = Object.freeze({
  eyebrow: '旅程工作區',
  title: '我的行程',
  lead: '先私下規劃，準備好再分享。',
  create: '建立行程',
  loading: '載入你的行程…',
  loadFailedTitle: '無法載入行程',
  tryAgain: '再試一次',
  emptyTitle: '開始第一趟旅程',
  emptyHint: '還沒有行程。建立一趟後即可進入規劃工作區。',
  emptyCta: '建立第一個行程',
  updatedPrefix: '更新於',
  untitled: '未命名行程',
});

export const CREATE_JOURNEY_PRODUCT_CHROME = Object.freeze({
  eyebrow: '新行程',
  title: '建立行程',
  lead: '預設 3 天，建立後可在編輯頁使用 ＋／－ 調整',
  stepLabel: '1 / 4',
  back: '關閉',
  backAria: '關閉並返回',
  fieldTitle: '行程名稱',
  fieldDestination: '目的地',
  fieldDates: '行程日期',
  fieldStart: '開始日期',
  fieldEnd: '結束日期',
  dateRangeSeparator: '→',
  coverSectionLabel: '行程封面',
  coverPickLabel: '選擇封面',
  coverChangeLabel: '更換封面',
  coverClearLabel: '清除封面',
  coverEmptyHint: '尚未選擇封面',
  coverHelper: '你可以從相簿選擇，建立後仍可更換。',
  coverInvalidType: '請選擇圖片檔案。',
  coverUploadFailed: '封面上傳失敗。行程已建立，可重試封面或稍後在行程工作區設定。',
  coverRetryLabel: '重試封面',
  coverRetrying: '重試中…',
  coverSkipLabel: '略過，稍後設定',
  submit: '建立並開始編輯',
  submitting: '建立中…',
  titleRequired: '請輸入標題。',
  titleTooLong: '標題請在 100 字以內。',
  startRequired: '請選擇開始日期。',
  endRequired: '請選擇結束日期。',
  endBeforeStart: '結束日期不可早於開始日期。',
  validationFailed: '請檢查行程資料後再試。',
  createFailed: '無法建立行程，請再試一次。',
});

/** Journey member role labels for list / overview chrome. */
export const JOURNEY_MEMBER_ROLE_PRODUCT_LABELS = Object.freeze({
  OWNER: '擁有者',
  COORDINATOR: '協調者',
  MEMBER: '成員',
});

export function journeyMemberRoleProductLabel(role) {
  const key = String(role || '');
  if (!key) return '';
  return JOURNEY_MEMBER_ROLE_PRODUCT_LABELS[key] || key;
}
